import React, { useState } from 'react';
import { ETF, AddPortfolioEntryParams, IsinLookupResult } from '../../../services/portfolio';

const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'JPY', 'SEK', 'NOK', 'DKK', 'CAD', 'AUD', 'HKD', 'SGD', 'CNY'] as const;

interface IsinLookupProps {
  isin: string;
  setIsin: (v: string) => void;
  resolved: IsinLookupResult | null;
  resolveError: string | null;
  shares: string;
  setShares: (v: string) => void;
  purchasePrice: string;
  setPurchasePrice: (v: string) => void;
  purchaseDate: string;
  setPurchaseDate: (v: string) => void;
  purchaseCurrency: string;
  setPurchaseCurrency: (v: string) => void;
  handleResolve: () => void;
  handleSubmit: (e: React.FormEvent) => void;
  clearResolved: () => void;
  reset: () => void;
  isResolving: boolean;
  isSubmitting: boolean;
  addError: any;
}

interface AddETFModalProps {
  etfs: ETF[] | undefined;
  etfSearchTerm: string;
  setEtfSearchTerm: (term: string) => void;
  formData: AddPortfolioEntryParams;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  handleAddSubmit: (e: React.FormEvent) => void;
  setShowAddModal: (show: boolean) => void;
  resetForm: () => void;
  isPending: boolean;
  isinLookup: IsinLookupProps;
}

const TypeBadge: React.FC<{ type: 'etf' | 'asset'; assetType?: string }> = ({ type, assetType }) => {
  const label = type === 'etf' ? 'ETF' : (assetType ?? 'Asset');
  const bg    = type === 'etf' ? '#1a2666' : '#6b7abf';
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 999, fontSize: '0.7rem',
      fontWeight: 600, color: '#fff', background: bg, letterSpacing: '0.02em',
    }}>
      {label.toUpperCase()}
    </span>
  );
};

const AddETFModal: React.FC<AddETFModalProps> = ({
  etfs,
  etfSearchTerm,
  setEtfSearchTerm,
  formData,
  handleInputChange,
  handleAddSubmit,
  setShowAddModal,
  resetForm,
  isPending,
  isinLookup,
}) => {
  const [mode, setMode] = useState<'search' | 'isin'>('search');

  const handleClose = () => {
    isinLookup.reset();
    setShowAddModal(false);
    resetForm();
  };

  const switchMode = (next: 'search' | 'isin') => {
    isinLookup.reset();
    setMode(next);
  };

  const handleIsinKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); isinLookup.handleResolve(); }
  };

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add ETF to Portfolio</h5>
              <button type="button" className="btn-close" onClick={handleClose} />
            </div>

            <div className="modal-body">
              {/* Mode toggle */}
              <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e4e4e7', marginBottom: 20 }}>
                {(['search', 'isin'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => switchMode(m)}
                    style={{
                      flex: 1, padding: '6px 0', fontSize: '0.82rem', fontWeight: 500,
                      border: 'none', cursor: 'pointer', transition: 'background 0.15s, color 0.15s',
                      background: mode === m ? '#000537' : '#fff',
                      color: mode === m ? '#fff' : '#52525b',
                    }}
                  >
                    {m === 'search' ? 'Search' : 'By ISIN'}
                  </button>
                ))}
              </div>

              {/* Search mode */}
              {mode === 'search' && (
                <form onSubmit={handleAddSubmit}>
                  <div className="mb-3">
                    <label className="form-label">ETF</label>
                    <div className="input-group mb-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search ETFs by name or ticker..."
                        value={etfSearchTerm}
                        onChange={(e) => setEtfSearchTerm(e.target.value)}
                      />
                      {etfSearchTerm && (
                        <button className="btn btn-outline-secondary" type="button" onClick={() => setEtfSearchTerm('')}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-x" viewBox="0 0 16 16">
                            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                          </svg>
                        </button>
                      )}
                    </div>
                    <select
                      name="etf_id"
                      value={formData.etf_id || ''}
                      onChange={handleInputChange}
                      className="form-select"
                      required
                    >
                      <option value="">Select an ETF</option>
                      {etfs?.filter(etf =>
                        etfSearchTerm === '' ||
                        etf.ticker.toLowerCase().includes(etfSearchTerm.toLowerCase()) ||
                        etf.name.toLowerCase().includes(etfSearchTerm.toLowerCase())
                      ).map(etf => (
                        <option key={etf.id} value={etf.id}>{etf.ticker} - {etf.name}</option>
                      ))}
                    </select>
                    {etfs && etfSearchTerm && etfs.filter(etf =>
                      etf.ticker.toLowerCase().includes(etfSearchTerm.toLowerCase()) ||
                      etf.name.toLowerCase().includes(etfSearchTerm.toLowerCase())
                    ).length === 0 && (
                      <div className="text-danger mt-1 small">No ETFs found matching "{etfSearchTerm}"</div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Shares</label>
                    <input type="number" name="shares" value={formData.shares || ''} onChange={handleInputChange} className="form-control" required step="0.01" min="0.01" />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Purchase Price <span style={{ color: '#a1a1aa', fontWeight: 400 }}>(optional)</span></label>
                    <div className="input-group">
                      <select name="purchase_currency" value={formData.purchase_currency || 'EUR'} onChange={handleInputChange} className="form-select" style={{ maxWidth: 80, borderRadius: '6px 0 0 6px' }}>
                        {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input type="number" name="purchase_price" value={formData.purchase_price || ''} onChange={handleInputChange} className="form-control" placeholder="0.00" step="0.01" min="0.01" />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Purchase Date</label>
                    <input type="date" name="purchase_date" value={formData.purchase_date || ''} onChange={handleInputChange} className="form-control" />
                  </div>
                  <div className="modal-footer px-0 pb-0">
                    <button type="button" onClick={handleClose} className="btn btn-secondary">Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={isPending}>
                      {isPending ? 'Adding...' : 'Add ETF'}
                    </button>
                  </div>
                </form>
              )}

              {/* ISIN mode — step 1: lookup */}
              {mode === 'isin' && !isinLookup.resolved && (
                <div>
                  <p style={{ fontSize: '0.85rem', color: '#52525b', marginBottom: 16 }}>
                    Enter an ISIN to look up an ETF or stock and add it to your portfolio.
                  </p>
                  <div className="mb-3">
                    <label className="form-label">ISIN</label>
                    <div className="input-group">
                      <input
                        type="text"
                        className={`form-control${isinLookup.resolveError ? ' is-invalid' : ''}`}
                        placeholder="e.g. IE00B4L5Y983"
                        value={isinLookup.isin}
                        onChange={e => isinLookup.setIsin(e.target.value.toUpperCase())}
                        onKeyDown={handleIsinKeyDown}
                        maxLength={12}
                        autoFocus
                      />
                      <button
                        className="btn btn-primary"
                        type="button"
                        onClick={isinLookup.handleResolve}
                        disabled={isinLookup.isResolving || isinLookup.isin.trim().length !== 12}
                      >
                        {isinLookup.isResolving ? <span className="spinner-border spinner-border-sm" /> : 'Look up'}
                      </button>
                    </div>
                    {isinLookup.resolveError && (
                      <div className="invalid-feedback d-block mt-1" style={{ fontSize: '0.8rem' }}>
                        {isinLookup.resolveError}
                      </div>
                    )}
                  </div>
                  <div className="modal-footer px-0 pb-0">
                    <button type="button" className="btn btn-secondary" onClick={handleClose}>Cancel</button>
                  </div>
                </div>
              )}

              {/* ISIN mode — step 2: confirm + details */}
              {mode === 'isin' && isinLookup.resolved && (
                <form onSubmit={isinLookup.handleSubmit}>
                  <div style={{ background: '#f8f9ff', border: '1px solid #e8eaf6', borderRadius: 8, padding: '12px 14px', marginBottom: 20 }}>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <TypeBadge type={isinLookup.resolved.type} assetType={isinLookup.resolved.asset_type} />
                      {!isinLookup.resolved.found_in_db && (
                        <span style={{ fontSize: '0.68rem', color: '#a1a1aa' }}>new record</span>
                      )}
                    </div>
                    <div style={{ fontWeight: 600, color: '#000537', fontSize: '1rem' }}>{isinLookup.resolved.ticker}</div>
                    <div style={{ fontSize: '0.8rem', color: '#52525b', marginTop: 2 }}>{isinLookup.resolved.name}</div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Shares</label>
                    <input type="number" className="form-control" placeholder="0.00" value={isinLookup.shares} onChange={e => isinLookup.setShares(e.target.value)} step="0.000001" min="0.000001" required autoFocus />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Purchase price <span style={{ color: '#a1a1aa', fontWeight: 400 }}>(optional)</span></label>
                    <div className="input-group">
                      <select value={isinLookup.purchaseCurrency} onChange={e => isinLookup.setPurchaseCurrency(e.target.value)} className="form-select" style={{ maxWidth: 80, borderRadius: '6px 0 0 6px' }}>
                        {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input type="number" className="form-control" placeholder="0.00" value={isinLookup.purchasePrice} onChange={e => isinLookup.setPurchasePrice(e.target.value)} step="0.01" min="0.01" />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Purchase date <span style={{ color: '#a1a1aa', fontWeight: 400 }}>(optional)</span></label>
                    <input type="date" className="form-control" value={isinLookup.purchaseDate} onChange={e => isinLookup.setPurchaseDate(e.target.value)} />
                  </div>

                  {isinLookup.addError && (
                    <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.8rem' }}>
                      {isinLookup.addError?.response?.data?.errors?.[0] ?? 'Failed to add position'}
                    </div>
                  )}

                  <div className="modal-footer px-0 pb-0">
                    <button type="button" className="btn btn-secondary" onClick={isinLookup.clearResolved}>Back</button>
                    <button type="submit" className="btn btn-primary" disabled={isinLookup.isSubmitting || !isinLookup.shares}>
                      {isinLookup.isSubmitting ? 'Adding…' : `Add ${isinLookup.resolved.type === 'etf' ? 'ETF' : 'position'}`}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddETFModal;
